/**
 * The fixed copy under the price cards. Short sentences, plain words, written for a college coach.
 * The facts here are the self-serve rules (see "Self-serve signup" in api/CLAUDE.md): the school
 * email decides the school, the school decides the tier, the tier sets a yearly price per sport,
 * one program per sport, billed yearly, and a free trial with no card (`trialDays`). With
 * `trialDays` null (still loading, or an API from before the trial) the copy says nothing about a
 * trial either way.
 *
 * With online signup closed (`signupAvailable` false), nothing here tells the coach to sign up: the
 * way in is "Talk to us", so the copy points there instead.
 *
 * With baseball plans on sale (`hasPlans`), baseball is priced by the plan the coach picks and only
 * softball by school tier, so the first step and the sport price answer say so. Without plans (an
 * older API, or none on sale) every line reads as before.
 */

type Step = { title: string; body: string };
type Faq = { question: string; answer: string };

const trialStep = (trialDays: number): Step => ({
  title: `Try it free for ${trialDays} days`,
  body: 'You get the full app from day one, with no card needed. Pay any time during the trial to keep going.',
});

const SCHOOL_PRICE_STEP: Step = {
  title: 'Your school sets your price',
  body: 'Each school sits in one tier, based on its level and conference. Each tier has a yearly price for baseball and one for softball.',
};

const PLAN_PRICE_STEPS: readonly Step[] = [
  {
    title: 'Baseball: pick your plan',
    body: 'Every plan has the core app. Higher plans add more recruiting tools. The cards above show what each one includes.',
  },
  {
    title: 'Softball: your school sets your price',
    body: 'Each school sits in one tier, based on its level and conference. Each tier has one yearly price.',
  },
];

const howItWorks = (
  signupAvailable: boolean,
  trialDays: number | null,
  hasPlans: boolean
): readonly Step[] => [
  ...(hasPlans ? PLAN_PRICE_STEPS : [SCHOOL_PRICE_STEP]),
  {
    title: 'We find your school from your email',
    body: signupAvailable
      ? 'Sign up with your school email. We match it to your school and show your price before you pay.'
      : 'Your school email tells us which school you coach at. We show you your price before you pay.',
  },
  {
    title: 'One program per sport',
    body: 'A program is baseball or softball. If your school wants both, each one is its own program with its own price.',
  },
  {
    title: 'Billed once a year',
    body: 'You pay for the full year up front, through a secure checkout.',
  },
  ...(trialDays !== null ? [trialStep(trialDays)] : []),
];

const SPORT_PRICE_FAQ: Faq = {
  question: 'Does softball cost the same as baseball?',
  answer:
    'Not always. Each tier lists a yearly price for each sport. You pay the one for your school and your sport.',
};

const PLAN_SPORT_PRICE_FAQ: Faq = {
  question: 'Does softball cost the same as baseball?',
  answer:
    'No. Baseball programs pick a plan, and each plan has its own yearly price. Softball has one yearly price, set by your school.',
};

const demoFaq = (trialDays: number | null): Faq => ({
  question: 'Can I see RosterPilot before I buy?',
  answer:
    trialDays !== null
      ? `Yes. Every new program gets ${trialDays} days free, with no card needed. We are also glad to walk you through it on a call. Use Talk to us at the bottom of this page to book a time.`
      : 'Yes. We are glad to walk you through it on a call. Use Talk to us at the bottom of this page to book a time.',
});

const TRIAL_END_FAQ: Faq = {
  question: 'What happens when the free trial ends?',
  answer:
    'To keep using RosterPilot, pay for the year. If you need more time or have questions, talk to us.',
};

const faq = (signupAvailable: boolean, trialDays: number | null, hasPlans: boolean): readonly Faq[] => {
  const trialFaqs = trialDays !== null ? [demoFaq(trialDays), TRIAL_END_FAQ] : [demoFaq(null)];
  const sportPriceFaq = hasPlans ? PLAN_SPORT_PRICE_FAQ : SPORT_PRICE_FAQ;
  return signupAvailable
    ? [
        {
          question: 'How do I know which tier my school is in?',
          answer:
            'Start signup with your school email. We look up your school and show your tier and your price on the next screen. You pay nothing until you choose to.',
        },
        sportPriceFaq,
        {
          question: 'What if I do not have a school email?',
          answer:
            'Online signup needs one, so we can tell which school you coach at. Talk to us and we will get your program set up.',
        },
        ...trialFaqs,
      ]
    : [
        {
          question: 'How do I know which tier my school is in?',
          answer:
            'Talk to us. We will tell you your tier and your price, and you pay nothing until you choose to.',
        },
        sportPriceFaq,
        ...trialFaqs,
      ];
};

interface PricingInfoProps {
  signupAvailable: boolean;
  /** Free trial length; null says nothing about a trial. */
  trialDays: number | null;
  /** Baseball is priced by plan. False (the default) reads exactly as before plans. */
  hasPlans?: boolean;
}

export const PricingHowItWorks = ({ signupAvailable, trialDays, hasPlans = false }: PricingInfoProps) => (
  <section className="pricing-section" aria-labelledby="pricing-how-title">
    <div className="mk-wrap">
      <div className="pricing-section__head">
        <p className="mk-eyebrow">The basics</p>
        <h2 className="pricing-section__title" id="pricing-how-title">
          How pricing works
        </h2>
      </div>
      <ol className="pricing-steps">
        {howItWorks(signupAvailable, trialDays, hasPlans).map((item, index) => (
          <li key={item.title} className="pricing-steps__item">
            <span className="pricing-steps__num" aria-hidden="true">
              {index + 1}
            </span>
            <div>
              <h3 className="pricing-steps__title">{item.title}</h3>
              <p className="pricing-steps__body">{item.body}</p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

/**
 * The marketing site's FAQ: one `details` per question, the first one open. The +/- marker is drawn
 * by CSS from the open state, inside an `aria-hidden` span, so it needs no script and is not read.
 */
export const PricingFaq = ({ signupAvailable, trialDays, hasPlans = false }: PricingInfoProps) => (
  <section className="pricing-section pricing-section--ice" aria-labelledby="pricing-faq-title">
    <div className="mk-wrap">
      <div className="pricing-section__head">
        <p className="mk-eyebrow">FAQ</p>
        <h2 className="pricing-section__title" id="pricing-faq-title">
          Questions coaches ask
        </h2>
      </div>
      <div className="pricing-faq" data-testid="pricing-faq">
        {faq(signupAvailable, trialDays, hasPlans).map((item, index) => (
          <details key={item.question} className="pricing-faq__item" open={index === 0}>
            <summary className="pricing-faq__question">
              {item.question}
              <span className="pricing-faq__ic" aria-hidden="true" />
            </summary>
            <div className="pricing-faq__answer">
              <p>{item.answer}</p>
            </div>
          </details>
        ))}
      </div>
    </div>
  </section>
);
